import React, {useEffect, useImperativeHandle, useState} from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import userApi from 'api/auth';
import isMetaMaskInstalled from 'features/web3/utils/isMetaMaskInstalled';
import connectWallet from 'features/web3/utils/connectMetamask';
import personalSign from 'features/web3/utils/personalSign';
import $t from 'translation';
import {useCurrentUserContext} from 'features/user/contexts/user-provider';
import {useNavigate} from 'react-router-dom';
import {FormHelperText} from '@mui/material';
import PropTypes from 'prop-types';
import InstallMetamask from 'features/metamask/components/install-metamask';

const ConnectMetamask = React.forwardRef((props, ref) => {
  const {buttonText, messageSignature, handleAction, showErrorMessage, onClick = null} = props;
  const navigate = useNavigate();
  const {login} = useCurrentUserContext();
  const hasMetamaskExtension = isMetaMaskInstalled();
  const [state, setState] = useState({
    loadingMetamask: false,
    showErrorMessage,
    errorMessage: '',
  });

  useEffect(() => {
    setState({
      ...state,
      showErrorMessage: props.showErrorMessage,
    });
  }, [props]);

  useImperativeHandle(ref, () => ({
    handleClick: handleClick,
  }));

  const connectWithMetamask = async (nonce, publicAddress, data) => {
    const message = `Ethelo ${messageSignature},${Date.now()},${nonce}`;
    const {signature, errorMessage} = await personalSign({message, publicAddress});
    if (signature) {
      const user = {
        ...data,
        terms: true,
        public_address: publicAddress,
        message,
        signature,
      };
      userApi[handleAction]({user})
        .then(() => {
          login();
          navigate('/', {replace: true});
        })
        .catch(error => {
          setState({
            ...state,
            loadingMetamask: false,
            errorMessage: error,
          });
        });
    } else {
      setState({
        ...state,
        errorMessage,
        loadingMetamask: false,
      });
    }
  };

  const handleClick = async data => {
    setState({
      ...state,
      errorMessage: '',
      loadingMetamask: true,
      showErrorMessage: true,
    });
    const {publicAddress, errorMessage} = await connectWallet();
    if (publicAddress) {
      userApi.nonce({user: {public_address: publicAddress}})
        .then(response => {
          connectWithMetamask(response.nonce, publicAddress, data);
        })
        .catch(error => {
          setState({
            ...state,
            loadingMetamask: false,
            errorMessage: error,
          });
        });
    } else {
      setState({
        ...state,
        loadingMetamask: false,
        errorMessage,
      });
    }
  };

  if (hasMetamaskExtension) {
    return (
      <>
        <LoadingButton
          data-testid={'connect-with-metamask'}
          loading={state.loadingMetamask}
          loadingPosition={'start'}
          startIcon={' '} // To prevent silly warning
          variant={'contained'}
          color={'primary'}
          onClick={() => onClick ? onClick() : handleClick()}
        >
          {buttonText}
        </LoadingButton>
        {state.showErrorMessage && (
          <FormHelperText className={'Mui-error'}>
            {$t(state.errorMessage)}
          </FormHelperText>
        )}
      </>
    );
  } else {
    return <InstallMetamask data-testid={'install-metamask'}/>;
  }

});
ConnectMetamask.propTypes = {
  buttonText: PropTypes.string.isRequired,
  messageSignature: PropTypes.string.isRequired,
  handleAction: PropTypes.string.isRequired,
  showErrorMessage: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
};

export default ConnectMetamask;